import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Copy, Check, Play, LogOut, Wifi, WifiOff } from 'lucide-react';
import { 
  MultiplayerSession, 
  MultiplayerTurnMode, 
  MultiplayerPlayer, 
  User, 
  GameMode, 
  Difficulty, 
  Genre 
} from '../types';
import {
  createMultiplayerSession,
  joinMultiplayerSession,
  leaveMultiplayerSession,
  startMultiplayerGame,
  subscribeToMultiplayerEvents,
  copySessionCode,
  getMultiplayerSession,
} from '../services/multiplayer';

interface MultiplayerLobbyProps {
  user: User;
  onGameStart: (session: MultiplayerSession) => void;
  onBack: () => void;
  isDarkMode: boolean;
}

export const MultiplayerLobby: React.FC<MultiplayerLobbyProps> = ({
  user,
  onGameStart,
  onBack,
  isDarkMode,
}) => {
  const [mode, setMode] = useState<'select' | 'create' | 'join'>('select');
  const [session, setSession] = useState<MultiplayerSession | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  
  // Settings for creating a session
  const [turnMode, setTurnMode] = useState<MultiplayerTurnMode>('per_message');
  const [gameMode, setGameMode] = useState<GameMode>('adventure');
  const [difficulty, setDifficulty] = useState<Difficulty>(2);
  const [genre, setGenre] = useState<Genre>('random');
  const [votingEnabled, setVotingEnabled] = useState<boolean>(true);
  const [autoSendLimit, setAutoSendLimit] = useState<number>(0);
  const [maxPlayers, setMaxPlayers] = useState<number>(10);
  
  // Subscribe to session events
  useEffect(() => {
    if (!session) return;
    
    let cleanup: (() => void) | null = null;
    
    const subscribe = () => {
      cleanup = subscribeToMultiplayerEvents(
        session.id,
        (event) => {
          setIsConnected(true);
          
          // Handle player disconnected event
          if (event.type === 'player_disconnected') {
            const playerName = (event as any).playerName as string;
            setError(`${playerName || 'A player'} has disconnected`);
            setTimeout(() => setError(null), 5000); // Clear after 5 seconds
          }
          
          // Handle player left event
          if (event.type === 'player_left') {
            const playerName = (event as any).playerName as string;
            setError(`${playerName || 'A player'} has left the game`);
            setTimeout(() => setError(null), 5000);
          }
          
          // Handle player joined event - refetch session to get updated player list
          if (event.type === 'player_joined') {
            getMultiplayerSession(session.id).then(result => {
              setSession(result.session);
            }).catch(err => {
              console.error('Failed to refresh session:', err);
            });
          }
          
          // Handle game started event
          if (event.type === 'game_started') {
            // Refetch the session to get the full state
            getMultiplayerSession(session.id).then(result => {
              setSession(result.session);
              if (result.session.state === 'playing') {
                onGameStart(result.session);
              }
            }).catch(err => {
              console.error('Failed to refresh session:', err);
            });
          }
        },
        (err) => {
          console.error('SSE error:', err);
          setIsConnected(false);
        }
      );
    };
    
    subscribe();
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [session?.id, onGameStart]);
  
  // Create a new session
  const handleCreate = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await createMultiplayerSession(
        turnMode, 
        gameMode, 
        difficulty, 
        genre,
        votingEnabled,
        autoSendLimit,
        maxPlayers
      );
      setSession(result.session);
      setIsConnected(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Join an existing session
  const handleJoin = async () => {
    if (!joinCode.trim()) {
      setError('Please enter a session code');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await joinMultiplayerSession(joinCode.trim().toUpperCase());
      setSession(result.session);
      setIsConnected(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join session');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Leave the session
  const handleLeave = async () => {
    if (!session) return;
    
    setIsLoading(true);
    
    try {
      await leaveMultiplayerSession(session.id);
      setSession(null);
      setMode('select');
      setIsConnected(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave session');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Start the game (host only)
  const handleStartGame = async () => {
    if (!session) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await startMultiplayerGame(session.id);
      onGameStart(result.session);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start game');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Copy session code
  const handleCopyCode = async () => {
    if (!session) return;
    
    const success = await copySessionCode(session.id);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // Check if current user is host
  const isHost = useMemo(() => session?.hostId === user.id, [session?.hostId, user.id]);
  const canStart = useMemo(() => isHost && session && session.players.length >= 2, [isHost, session]);
  
  // Mode selection screen
  if (mode === 'select' && !session) {
    return (
      <div className={`flex-1 flex flex-col p-6 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <h2 className={`text-2xl font-bold mb-2 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          Multiplayer
        </h2>
        <p className={`text-sm mb-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Play with friends in real-time! (2-10 players)
        </p>
        
        <div className="flex flex-col gap-4 max-w-md mx-auto w-full">
          <button
            onClick={() => setMode('create')}
            className={`p-6 rounded-xl border-2 text-left transition-all hover:scale-[1.02] active:scale-[0.98] ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-700 hover:border-blue-500' 
                : 'bg-gray-50 border-gray-200 hover:border-blue-400'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${isDarkMode ? 'bg-blue-900 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                <Users size={24} />
              </div>
              <div>
                <h3 className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Create Game
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Start a new session and invite a friend
                </p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => setMode('join')}
            className={`p-6 rounded-xl border-2 text-left transition-all hover:scale-[1.02] active:scale-[0.98] ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-700 hover:border-green-500' 
                : 'bg-gray-50 border-gray-200 hover:border-green-400'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-600'}`}>
                <Play size={24} />
              </div>
              <div>
                <h3 className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Join Game
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Enter a code to join a friend's game
                </p>
              </div>
            </div>
          </button>
        </div>
        
        <button
          onClick={onBack}
          className={`mt-8 px-4 py-2 rounded-lg text-sm font-medium transition-colors mx-auto ${
            isDarkMode 
              ? 'text-gray-400 hover:text-gray-200' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ← Back
        </button>
      </div>
    );
  }
  
  // Create session screen
  if (mode === 'create' && !session) {
    return (
      <div className={`flex-1 flex flex-col p-6 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <h2 className={`text-2xl font-bold mb-6 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          Create Multiplayer Game
        </h2>
        
        {error && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>
            {error}
          </div>
        )}
        
        <div className="space-y-6 max-w-md mx-auto w-full">
          {/* Turn Mode */}
          <div>
            <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Turn Mode
            </label>
            <div className="space-y-2">
              <button
                onClick={() => setTurnMode('per_message')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  turnMode === 'per_message'
                    ? isDarkMode
                      ? 'border-blue-500 bg-blue-900/30'
                      : 'border-blue-500 bg-blue-50'
                    : isDarkMode
                    ? 'border-gray-700 hover:border-gray-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Per Message
                </div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Take turns sending complete messages. Player switches after each message is sent.
                </div>
              </button>
              
              <button
                onClick={() => setTurnMode('per_prediction')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  turnMode === 'per_prediction'
                    ? isDarkMode
                      ? 'border-purple-500 bg-purple-900/30'
                      : 'border-purple-500 bg-purple-50'
                    : isDarkMode
                    ? 'border-gray-700 hover:border-gray-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Per Prediction
                </div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Take turns selecting words. Each player picks one word, then decides to send or continue.
                </div>
              </button>
            </div>
          </div>
          
          {/* Game Mode */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Game Mode
            </label>
            <select
              value={gameMode}
              onChange={(e) => setGameMode(e.target.value as GameMode)}
              className={`w-full border px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-200' 
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="adventure">Adventure Mode</option>
              <option value="endless">Endless Mode</option>
            </select>
          </div>
          
          {/* Difficulty */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Difficulty
              </label>
              <span className={`text-xs px-2 py-1 rounded ${
                difficulty === 1 
                  ? 'bg-green-100 text-green-700'
                  : difficulty === 2 
                  ? 'bg-yellow-100 text-yellow-700'
                  : difficulty === 3
                  ? 'bg-red-100 text-red-700'
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {difficulty === 1 ? 'Easy' : difficulty === 2 ? 'Medium' : difficulty === 3 ? 'Hard' : 'Chaos'}
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="4"
              value={difficulty}
              onChange={(e) => setDifficulty(Number(e.target.value) as Difficulty)}
              className="w-full"
            />
          </div>
          
          {/* Genre */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Genre
            </label>
            <select
              value={genre}
              onChange={(e) => setGenre(e.target.value as Genre)}
              className={`w-full border px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-200' 
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="random">🎲 Random</option>
              <option value="fantasy">🧙 Fantasy</option>
              <option value="sci-fi">🚀 Sci-Fi</option>
              <option value="horror">👻 Horror</option>
              <option value="comedy">😂 Comedy</option>
              <option value="mystery">🔍 Mystery</option>
            </select>
          </div>
          
          {/* Max Players */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Max Players
              </label>
              <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                {maxPlayers}
              </span>
            </div>
            <input
              type="range"
              min="2"
              max="10"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(Number(e.target.value))}
              className="w-full"
            />
          </div>
          
          {/* Adventure Voting Toggle */}
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Adventure Voting
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Players vote on the next adventure (15 seconds)
                </div>
              </div>
              <button
                onClick={() => setVotingEnabled(!votingEnabled)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  votingEnabled 
                    ? 'bg-green-500' 
                    : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  votingEnabled ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
          
          {/* Auto-Send Timer */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Auto-Send Timer
              </label>
              <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                {autoSendLimit === 0 ? 'Disabled' : `${autoSendLimit}s`}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="120"
              step="10"
              value={autoSendLimit}
              onChange={(e) => setAutoSendLimit(Number(e.target.value))}
              className="w-full"
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Auto-send message if player stalls. 0 = disabled.
            </p>
          </div>
          
          <button
            onClick={handleCreate}
            disabled={isLoading}
            className={`w-full px-6 py-3 rounded-xl font-bold text-lg shadow-lg transition-all ${
              isLoading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow-xl active:scale-95'
            } ${
              isDarkMode 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            {isLoading ? 'Creating...' : 'Create Game'}
          </button>
        </div>
        
        <button
          onClick={() => setMode('select')}
          className={`mt-6 px-4 py-2 rounded-lg text-sm font-medium transition-colors mx-auto ${
            isDarkMode 
              ? 'text-gray-400 hover:text-gray-200' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ← Back
        </button>
      </div>
    );
  }
  
  // Join session screen
  if (mode === 'join' && !session) {
    return (
      <div className={`flex-1 flex flex-col p-6 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <h2 className={`text-2xl font-bold mb-6 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          Join Multiplayer Game
        </h2>
        
        {error && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>
            {error}
          </div>
        )}
        
        <div className="space-y-6 max-w-md mx-auto w-full">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Session Code
            </label>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="Enter 6-character code"
              maxLength={6}
              className={`w-full px-4 py-3 text-center text-2xl font-mono tracking-widest uppercase border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-gray-200' 
                  : 'bg-white border-gray-300 text-gray-800'
              }`}
            />
          </div>
          
          <button
            onClick={handleJoin}
            disabled={isLoading || joinCode.length < 6}
            className={`w-full px-6 py-3 rounded-xl font-bold text-lg shadow-lg transition-all ${
              isLoading || joinCode.length < 6
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:shadow-xl active:scale-95'
            } ${
              isDarkMode 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-green-500 text-white hover:bg-green-600'
            }`}
          >
            {isLoading ? 'Joining...' : 'Join Game'}
          </button>
        </div>
        
        <button
          onClick={() => setMode('select')}
          className={`mt-6 px-4 py-2 rounded-lg text-sm font-medium transition-colors mx-auto ${
            isDarkMode 
              ? 'text-gray-400 hover:text-gray-200' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ← Back
        </button>
      </div>
    );
  }
  
  // Waiting room (session created or joined)
  if (session) {
    return (
      <div className={`flex-1 flex flex-col p-6 overflow-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        {/* Connection status */}
        <div className={`flex items-center justify-center gap-2 mb-4 text-sm ${
          isConnected 
            ? isDarkMode ? 'text-green-400' : 'text-green-600'
            : isDarkMode ? 'text-red-400' : 'text-red-600'
        }`}>
          {isConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
          <span>{isConnected ? 'Connected' : 'Connecting...'}</span>
        </div>
        
        <h2 className={`text-2xl font-bold mb-2 text-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          Waiting for Players
        </h2>
        
        {/* Session code */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className={`px-6 py-3 rounded-xl font-mono text-3xl tracking-widest ${
            isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-800'
          }`}>
            {session.id}
          </div>
          <button
            onClick={handleCopyCode}
            className={`p-3 rounded-full transition-colors ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
            title="Copy code"
          >
            {copied ? <Check size={20} className="text-green-500" /> : <Copy size={20} />}
          </button>
        </div>
        
        <p className={`text-sm text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Share this code with friends to invite them
        </p>
        
        {error && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'}`}>
            {error}
          </div>
        )}
        
        {/* Game settings */}
        <div className={`mb-6 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Turn Mode:</span>
              <span className={`ml-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {session.turnMode === 'per_message' ? 'Per Message' : 'Per Prediction'}
              </span>
            </div>
            <div>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Game Mode:</span>
              <span className={`ml-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {session.gameMode === 'adventure' ? 'Adventure' : 'Endless'}
              </span>
            </div>
            <div>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Difficulty:</span>
              <span className={`ml-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {session.difficulty === 1 ? 'Easy' : session.difficulty === 2 ? 'Medium' : session.difficulty === 3 ? 'Hard' : 'Chaos'}
              </span>
            </div>
            <div>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Genre:</span>
              <span className={`ml-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {session.genre}
              </span>
            </div>
            <div>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Voting:</span>
              <span className={`ml-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {session.votingEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div>
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Auto-Send:</span>
              <span className={`ml-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {session.autoSendLimit === 0 ? 'Off' : `${session.autoSendLimit}s`}
              </span>
            </div>
          </div>
        </div>
        
        {/* Players list */}
        <div className="mb-6">
          <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Players ({session.players.length}/{session.maxPlayers || 10})
          </h3>
          <div className="space-y-2">
            {session.players.map((player) => (
              <div
                key={player.id}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                }`}
              >
                <img
                  src={player.picture || '/default-avatar.png'}
                  alt={player.name}
                  className="w-10 h-10 rounded-full"
                />
                <div className="flex-1">
                  <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {player.username || player.name}
                  </div>
                  {player.isHost && (
                    <span className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      Host
                    </span>
                  )}
                </div>
                {player.isActive ? (
                  <div className="w-3 h-3 rounded-full bg-green-500" title="Online" />
                ) : (
                  <div className="w-3 h-3 rounded-full bg-gray-400" title="Offline" />
                )}
              </div>
            ))}
            
            {session.players.length < 2 && (
              <div className={`flex items-center justify-center p-4 rounded-lg border-2 border-dashed ${
                isDarkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'
              }`}>
                <span>Waiting for more players... ({session.players.length}/{session.maxPlayers || 10})</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="space-y-3">
          {isHost && (
            <button
              onClick={handleStartGame}
              disabled={!canStart || isLoading}
              className={`w-full px-6 py-3 rounded-xl font-bold text-lg shadow-lg transition-all ${
                !canStart || isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:shadow-xl active:scale-95'
              } ${
                isDarkMode 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {isLoading ? 'Starting...' : canStart ? `Start Game (${session.players.length} players)` : 'Waiting for players...'}
            </button>
          )}
          
          {!isHost && (
            <div className={`w-full px-6 py-3 rounded-xl text-center ${
              isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
            }`}>
              Waiting for host to start...
            </div>
          )}
          
          <button
            onClick={handleLeave}
            disabled={isLoading}
            className={`w-full px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
              isDarkMode 
                ? 'text-red-400 hover:bg-red-900/30' 
                : 'text-red-600 hover:bg-red-50'
            }`}
          >
            <LogOut size={16} />
            Leave Game
          </button>
        </div>
      </div>
    );
  }
  
  return null;
};
